import axios, { AxiosInstance, AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { ApiError } from './types';
import { API_BASE_URL, USE_BACKEND } from './config';

// Create axios instance with default configuration
// Only create if backend is enabled
const createApiClient = (): AxiosInstance | null => {
    if (!USE_BACKEND) {
        return null;
    }

    const client = axios.create({
        baseURL: API_BASE_URL,
        timeout: 30000, // 30 seconds timeout
        headers: {
            'Content-Type': 'application/json',
        },
    });

    // Request interceptor - add auth token, etc.
    client.interceptors.request.use(
        (config: InternalAxiosRequestConfig) => {
            // Add authentication token if available
            const token = localStorage.getItem('authToken');
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }

            // Add store ID if available
            const storeId = localStorage.getItem('storeId');
            if (storeId) {
                config.headers['X-Store-Id'] = storeId;
            }

            return config;
        },
        (error: AxiosError) => {
            return Promise.reject(error);
        }
    );

    // Response interceptor - handle errors globally
    client.interceptors.response.use(
        (response: AxiosResponse) => {
            return response;
        },
        (error: AxiosError) => {
            const apiError: ApiError = {
                message: error.message || 'An unexpected error occurred',
                status: error.response?.status || 500,
                errors: error.response?.data as Record<string, string[]> | undefined,
            };

            // Handle specific error cases
            if (error.response?.status === 401) {
                // Unauthorized - clear token and redirect to login
                localStorage.removeItem('authToken');
                // You might want to dispatch a logout action here
            }

            return Promise.reject(apiError);
        }
    );

    return client;
};

// Export the configured client (null if backend is disabled)
export const apiClient = createApiClient();

// Helper to check if backend is available
export const isBackendEnabled = (): boolean => {
    return USE_BACKEND && apiClient !== null;
};

// Helper function to handle API errors
export const handleApiError = (error: unknown): ApiError => {
    if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<{ message?: string; errors?: Record<string, string[]> }>;
        const responseData = axiosError.response?.data;
        return {
            message: responseData?.message || axiosError.message || 'An error occurred',
            status: axiosError.response?.status || 500,
            errors: responseData?.errors,
        };
    }

    return {
        message: error instanceof Error ? error.message : 'An unexpected error occurred',
        status: 500,
    };
};

